import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Mail, User, Building2, Phone, ArrowRight,
    Sparkles, Shield, BarChart3, Zap, X,
    CheckCircle, Globe, Gauge, Wrench, Lock,
} from 'lucide-react';

import { API_BASE_URL } from './config/api';

const PUBLIC_AUDIT_TRACKING_QS_KEY = 'public_audit_tracking_qs';

function getTrackingSearchFromCurrentUrl() {
    if (typeof window === 'undefined') return '';
    const src = new URLSearchParams(window.location.search || '');
    const out = new URLSearchParams();
    const keepExact = new Set(['gclid', 'fbclid', 'msclkid', 'ttclid']);
    src.forEach((value, key) => {
        const k = String(key || '').toLowerCase();
        if (k.startsWith('utm_') || keepExact.has(k)) out.set(key, value);
    });
    const qs = out.toString();
    return qs ? `?${qs}` : '';
}

function getPersistedTrackingSearch() {
    if (typeof window === 'undefined') return '';
    try {
        const qs = sessionStorage.getItem(PUBLIC_AUDIT_TRACKING_QS_KEY) || '';
        return qs.startsWith('?') ? qs : (qs ? `?${qs}` : '');
    } catch (_) {
        return '';
    }
}

function persistTrackingSearch(search) {
    if (typeof window === 'undefined') return;
    if (!search) return;
    try {
        sessionStorage.setItem(PUBLIC_AUDIT_TRACKING_QS_KEY, search);
    } catch (_) {}
}

function getOrCreateLandingSessionId() {
    let sid = sessionStorage.getItem('pub_landing_session_id');
    if (!sid) {
        sid = 'land_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
        sessionStorage.setItem('pub_landing_session_id', sid);
    }
    return sid;
}

async function trackPublicEvent({ eventType, eventLabel, page = 'landing', extraData }) {
    try {
        await fetch(`${API_BASE_URL}/api/public/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token: null,
                event_type: eventType,
                event_label: eventLabel,
                page,
                session_id: getOrCreateLandingSessionId(),
                duration: null,
                extra_data: extraData ? JSON.stringify(extraData) : null,
                user_agent: navigator.userAgent,
            }),
        });
    } catch (_) { /* ignore */ }
}

function sendBeaconPublicEvent({ eventType, eventLabel, page = 'landing', duration, extraData }) {
    try {
        const payload = JSON.stringify([{
            token: null,
            event_type: eventType,
            event_label: eventLabel,
            page,
            session_id: getOrCreateLandingSessionId(),
            duration: duration ?? null,
            extra_data: extraData ? JSON.stringify(extraData) : null,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }]);
        const url = `${API_BASE_URL}/api/public/event/batch`;
        // sendBeacon force credentials:include → CORS error si cross-origin sans Allow-Credentials.
        // On utilise fetch keepalive à la place (compatible unload, pas de credentials).
        if (typeof fetch !== 'undefined') {
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true,
                credentials: 'omit',
            }).catch(() => {});
            return true;
        }
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            return navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        }
        return false;
    } catch (_) {
        return false;
    }
}

function upsertMetaTag(attr, key, content) {
    if (typeof document === 'undefined') return;
    const selector = `meta[${attr}="${key}"]`;
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

const PRIVACY_POLICY_CONTENT = (
    <div className="space-y-4 text-sm text-neutral-600 leading-relaxed">
        <p>Ascanio s&apos;engage à protéger vos données personnelles. La présente politique décrit comment nous collectons, utilisons et protégeons les informations recueillies dans le cadre de votre demande d&apos;audit SEO gratuit.</p>
        <section>
            <h3 className="font-semibold text-neutral-900 mb-2">1. Données collectées</h3>
            <p>Nous collectons : prénom, nom, adresse email, numéro de téléphone, URL du site à analyser et nom d&apos;entreprise (optionnel).</p>
        </section>
        <section>
            <h3 className="font-semibold text-neutral-900 mb-2">2. Finalité du traitement</h3>
            <p>Vos données sont utilisées exclusivement pour générer votre audit SEO, vous communiquer les résultats, et éventuellement vous proposer un accompagnement personnalisé.</p>
        </section>
        <section>
            <h3 className="font-semibold text-neutral-900 mb-2">3. Durée de conservation</h3>
            <p>Vos données sont conservées pendant la durée nécessaire à la fourniture du service et aux échanges commerciaux éventuels.</p>
        </section>
        <section>
            <h3 className="font-semibold text-neutral-900 mb-2">4. Vos droits</h3>
            <p>Conformément au RGPD, vous disposez d&apos;un droit d&apos;accès, de rectification, de suppression et de portabilité de vos données. Contactez-nous à l&apos;adresse indiquée sur notre site.</p>
        </section>
        <section>
            <h3 className="font-semibold text-neutral-900 mb-2">5. Sécurité</h3>
            <p>Nous mettons en œuvre les mesures techniques et organisationnelles adaptées pour protéger vos données.</p>
        </section>
    </div>
);

const inputFocusStyle = (e, focus) => {
    e.target.style.borderColor = focus ? '#00dede' : '#e2e8f0';
    e.target.style.boxShadow = focus ? '0 0 0 3px rgba(0, 222, 222, 0.15)' : 'none';
};

// Validation : email (format simple)
function isValidEmail(value) {
    if (!value || typeof value !== 'string') return false;
    const trimmed = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && trimmed.length <= 254;
}

// Validation : téléphone (chiffres, espaces, points, tirets, + en tête ; 10 à 15 chiffres)
function isValidPhone(value) {
    if (!value || typeof value !== 'string') return false;
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
}

// Caractères autorisés pour le téléphone (saisie)
const PHONE_ALLOWED = /[0-9+\-.() \s]/g;
function sanitizePhoneInput(value) {
    if (!value || typeof value !== 'string') return '';
    const kept = value.match(new RegExp(PHONE_ALLOWED.source, 'g'));
    return kept ? kept.join('').slice(0, 20) : '';
}

const AUDIT_STEPS = [
    { icon: Search, label: 'Analyse SEO & balises', desc: 'Titre, meta, H1, images…' },
    { icon: Gauge, label: 'Performance & Core Web Vitals', desc: 'Vitesse desktop & mobile' },
    { icon: Wrench, label: 'Fondations techniques', desc: 'HTTPS, indexation, mobile…' },
    { icon: Globe, label: 'Visibilité sur Google', desc: 'Trafic organique, mots-clés' },
    { icon: Sparkles, label: 'Audit visuel UX/UI', desc: 'Design, lisibilité, hiérarchie visuelle' },
    { icon: Sparkles, label: 'Analyse IA du contenu', desc: 'Positionnement & message' },
];

// Nombre d'étapes "fake" avant pause
const PAUSE_AFTER = 2;

/* ── Teaser d'audit ────────────────────────────────────────────── */
function AuditTeaser({ url, started }) {
    // phase: 'intro' | 'paused' | 'running' | 'done'
    const [phase, setPhase] = useState('intro');
    const [completedSteps, setCompletedSteps] = useState(0);
    const intervalRef = useRef(null);

    // Fake démarrage au montage : complète PAUSE_AFTER étapes puis s'arrête
    useEffect(() => {
        setPhase('intro');
        setCompletedSteps(0);
        let count = 0;
        intervalRef.current = setInterval(() => {
            count += 1;
            setCompletedSteps(count);
            if (count >= PAUSE_AFTER) {
                clearInterval(intervalRef.current);
                setPhase('paused');
            }
        }, 850);
        return () => clearInterval(intervalRef.current);
    }, [url]); // reset si URL change

    // Quand le formulaire est soumis : reprend à partir de là où on s'est arrêté
    useEffect(() => {
        if (!started) return;
        setPhase('running');
        let count = PAUSE_AFTER;
        intervalRef.current = setInterval(() => {
            count += 1;
            setCompletedSteps(count);
            if (count >= AUDIT_STEPS.length) {
                clearInterval(intervalRef.current);
                setPhase('done');
            }
        }, 1000);
        return () => clearInterval(intervalRef.current);
    }, [started]);

    const displayUrl = url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';
    const isPaused = phase === 'paused';
    const isRunning = phase === 'running' || phase === 'intro';
    const isDone = phase === 'done';

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-center gap-3 mb-1">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300"
                    style={{ background: isPaused ? 'rgba(245,158,11,0.1)' : isRunning ? 'rgba(0,222,222,0.1)' : 'rgba(16,185,129,0.1)' }}>
                    <Search size={16} style={{ color: isPaused ? '#d97706' : isRunning ? '#00a0a0' : '#059669' }} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-xs text-neutral-400 font-medium">
                        {isPaused ? 'En attente de vos informations' : isDone ? 'Analyse terminée pour' : 'Analyse en cours pour'}
                    </div>
                    <div className="text-sm font-bold text-neutral-900 truncate">{displayUrl || 'votre site'}</div>
                </div>
                <div className="flex-shrink-0">
                    {isPaused && (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 animate-pulse"
                            style={{ background: 'rgba(245,158,11,0.12)', color: '#b45309' }}>
                            <Lock size={10} />En pause
                        </span>
                    )}
                    {isDone && (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                            style={{ background: 'rgba(0,222,222,0.12)', color: '#00a0a0' }}>
                            Prêt !
                        </span>
                    )}
                    {isRunning && !isPaused && (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-neutral-500 bg-neutral-100 animate-pulse">
                            En cours…
                        </span>
                    )}
                </div>
            </div>

            {/* Steps */}
            <div className="space-y-2 sm:space-y-2.5">
                {AUDIT_STEPS.map(({ icon: Icon, label, desc }, i) => {
                    const isDoneStep = i < completedSteps;
                    const isActiveStep = i === completedSteps && phase !== 'paused' && phase !== 'done';
                    const isWaiting = i === completedSteps && isPaused; // l'étape qui attend
                    const isFuture = i > completedSteps;

                    return (
                        <div key={label} className={`flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl border transition-all duration-500 ${
                            isDoneStep
                                ? 'bg-emerald-50/80 border-emerald-200/60'
                                : isWaiting
                                    ? 'bg-amber-50/70 border-amber-200/60'
                                    : isActiveStep
                                        ? 'bg-[#00dede]/5 border-[#00dede]/30'
                                        : 'bg-neutral-50/60 border-neutral-200/40'
                        } ${isFuture ? 'opacity-35' : ''}`}>
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isDoneStep ? 'bg-emerald-100'
                                : isWaiting ? 'bg-amber-100'
                                : isActiveStep ? 'bg-[#00dede]/10'
                                : 'bg-neutral-100'
                            }`}>
                                {isDoneStep
                                    ? <CheckCircle size={14} className="text-emerald-500" />
                                    : isWaiting
                                        ? <Lock size={13} className="text-amber-500" />
                                        : isActiveStep
                                            ? <div className="w-3 h-3 border-2 rounded-full border-[#00a0a0]/30 border-t-[#00a0a0] animate-spin" />
                                            : <Icon size={14} className="text-neutral-400" />
                                }
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className={`text-xs font-semibold leading-tight ${
                                    isDoneStep ? 'text-emerald-700'
                                    : isWaiting ? 'text-amber-700'
                                    : isActiveStep ? 'text-neutral-900'
                                    : 'text-neutral-500'
                                }`}>
                                    {label}
                                </div>
                                <div className={`text-[11px] mt-0.5 truncate ${isWaiting ? 'text-amber-500' : 'text-neutral-400'}`}>
                                    {isWaiting ? 'En attente de vos informations…' : desc}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Bandeau de pause */}
            {isPaused && (
                <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl border border-amber-200/70 text-xs font-medium"
                    style={{ background: 'rgba(251,191,36,0.08)', color: '#92400e' }}>
                    <Lock size={13} className="flex-shrink-0 text-amber-500" />
                    Renseignez vos informations pour reprendre l'analyse
                </div>
            )}

            
        </div>
    );
}

/* ── Préview rapport (étape 1) — version simplifiée ──────────── */
const PREVIEW_SCORE = 63;
const PREVIEW_METRICS = [
    { label: 'SEO', score: 68, color: '#5b8fd4' },
    { label: 'Perf.', score: 54, color: '#e5a855' },
    { label: 'Tech.', score: 83, color: '#5eb89a' },
    { label: 'Marché', score: 42, color: '#e07a7a' },
];

function AnimatedScore({ target, duration = 1500 }) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        let start = null;
        const tick = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / duration, 1);
            setValue(Math.round((1 - Math.pow(1 - p, 3)) * target));
            if (p < 1) requestAnimationFrame(tick);
        };
        const raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return <>{value}</>;
}

function AnimatedBar({ percent, color, delay = 0 }) {
    const [w, setW] = useState(0);
    useEffect(() => { const t = setTimeout(() => setW(percent), delay); return () => clearTimeout(t); }, [percent, delay]);
    return (
        <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden mt-1.5">
            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${w}%`, background: color }} />
        </div>
    );
}

function AuditPreview({ url }) {
    const displayUrl = url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'votre-site.fr';

    return (
        <div className="relative w-full select-none">
            {/* Badge flottant */}
            <div className="flex justify-center mb-4">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ background: 'rgba(0,222,222,0.1)', color: '#00a0a0', border: '1px solid rgba(0,222,222,0.2)' }}>
                    <Sparkles size={11} />Exemple de rapport
                </div>
            </div>

            <div className="rounded-2xl bg-white border overflow-hidden"
                style={{ boxShadow: '0 16px 48px rgba(0,0,0,0.09)', borderColor: 'rgba(0,0,0,0.06)' }}>

                {/* En-tête domaine */}
                <div className="px-6 py-4 border-b border-neutral-100 flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                        <Globe size={13} className="text-neutral-400" />
                    </div>
                    <span className="text-sm font-semibold text-neutral-800 truncate">{displayUrl}</span>
                    <span className="ml-auto text-[10px] text-neutral-400 flex-shrink-0">by Ascanio</span>
                </div>

                <div className="px-6 py-6 flex flex-col items-center gap-6">
                    {/* Score global */}
                    <div className="flex flex-col items-center gap-2">
                        <div className="relative">
                            <svg width="110" height="110" viewBox="0 0 110 110">
                                <circle cx="55" cy="55" r="44" fill="none" stroke="#f1f5f9" strokeWidth="9" />
                                <circle cx="55" cy="55" r="44" fill="none" stroke="#e5a855" strokeWidth="9"
                                    strokeDasharray={`${(54 / 100) * 2 * Math.PI * 44} ${2 * Math.PI * 44}`}
                                    strokeLinecap="round" transform="rotate(-90 55 55)" />
                                <circle cx="55" cy="55" r="44" fill="none" stroke="#5b8fd4" strokeWidth="9"
                                    strokeDasharray={`${(68 / 100) * 2 * Math.PI * 44} ${2 * Math.PI * 44}`}
                                    strokeDashoffset={-2 * Math.PI * 44 * 0.15}
                                    strokeLinecap="round" transform="rotate(-90 55 55)" opacity="0.7" />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl font-extrabold text-neutral-900 leading-none tabular-nums">
                                    <AnimatedScore target={PREVIEW_SCORE} />
                                </span>
                                <span className="text-[10px] text-neutral-400 font-medium">/100</span>
                            </div>
                        </div>
                        <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#fef3c7', color: '#b45309' }}>
                            À améliorer
                        </span>
                    </div>

                    {/* 4 métriques */}
                    <div className="w-full grid grid-cols-2 gap-3">
                        {PREVIEW_METRICS.map(({ label, score, color }, i) => (
                            <div key={label} className="rounded-xl p-3 border border-neutral-100 bg-neutral-50/60">
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className="text-[11px] font-medium text-neutral-500">{label}</span>
                                    <span className="text-sm font-extrabold tabular-nums" style={{ color }}>{score}</span>
                                </div>
                                <AnimatedBar percent={score} color={color} delay={300 + i * 100} />
                            </div>
                        ))}
                    </div>

                    {/* Recs floutées */}
                    <div className="w-full relative rounded-xl border border-neutral-100 overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-neutral-100 bg-neutral-50/40">
                            <span className="text-[11px] font-semibold text-neutral-600">Recommandations</span>
                        </div>
                        <div className="p-3 space-y-2 blur-sm pointer-events-none">
                            {[
                                { label: 'Optimiser la balise title', color: '#ef4444', bg: '#fef2f2' },
                                { label: 'Améliorer la vitesse mobile', color: '#ef4444', bg: '#fef2f2' },
                                { label: 'Enrichir la meta description', color: '#d97706', bg: '#fffbeb' },
                            ].map((rec, i) => (
                                <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: rec.bg }}>
                                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${rec.color}20` }}>
                                        <span className="text-[9px] font-bold" style={{ color: rec.color }}>{i + 1}</span>
                                    </div>
                                    <span className="text-[11px] font-semibold text-neutral-800">{rec.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-white/55 backdrop-blur-[1.5px]">
                            <div className="text-center">
                                <Lock size={16} className="text-neutral-400 mx-auto mb-1" />
                                <div className="text-xs font-semibold text-neutral-700">Entrez votre URL</div>
                                <div className="text-[11px] text-neutral-400">pour accéder au rapport</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Composant principal ──────────────────────────────────────── */
export default function PublicAuditLanding() {
    const navigate = useNavigate();

    // Mode rapide : ?quick=1 → bypass du formulaire de contact
    const quickMode = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('quick') === '1'
        : false;

    const [step, setStep] = useState(1);
    const [url, setUrl] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [company, setCompany] = useState('');
    const [rgpdConsent, setRgpdConsent] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const landingStartRef = useRef(Date.now());
    const landingExitTrackedRef = useRef(false);
    const auditSubmittedRef = useRef(false);
    const stepRef = useRef(1);
    const urlRef = useRef('');
    const [loading, setLoading] = useState(false);
    const [auditStarted, setAuditStarted] = useState(false); // true après soumission validée
    const [error, setError] = useState('');
    const [errorStatus, setErrorStatus] = useState(null); // 403, 404, etc. pour affichage dédié

    const inputBase = "w-full pl-10 pr-4 py-3 sm:py-3.5 min-h-[48px] rounded-full border text-gray-900 placeholder:text-gray-400 transition-all outline-none bg-white text-sm";

    // Champs obligatoires valides pour désactiver le bouton
    const step1Valid = url.trim().length > 0;
    const step2Valid = Boolean(
        firstName.trim() &&
        lastName.trim() &&
        isValidEmail(email) &&
        isValidPhone(phone) &&
        rgpdConsent
    );

    // Title et meta (réappliqués à chaque étape pour rester corrects en step 2)
    useEffect(() => {
        const title = "Audit SEO & GEO gratuit en 1 minute | Ascanio";
        const desc = "Obtenez un audit SEO & GEO gratuit et instantané de votre site : performance, technique, contenu, analysés par Ascanio.";
        const canonicalUrl = typeof window !== 'undefined' ? window.location.href : "https://audit.ascan.io/";
        const imageUrl = typeof window !== 'undefined'
            ? `${window.location.origin}/assets/logo-ascanio.png`
            : "https://audit.ascan.io/assets/logo-ascanio.png";

        document.title = title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', desc);
        upsertMetaTag('property', 'og:title', title);
        upsertMetaTag('property', 'og:description', desc);
        upsertMetaTag('property', 'og:type', 'website');
        upsertMetaTag('property', 'og:url', canonicalUrl);
        upsertMetaTag('property', 'og:image', imageUrl);
        upsertMetaTag('name', 'twitter:card', 'summary_large_image');
        upsertMetaTag('name', 'twitter:title', title);
        upsertMetaTag('name', 'twitter:description', desc);
        upsertMetaTag('name', 'twitter:image', imageUrl);
        return () => {
            document.title = "Metrics";
            if (metaDesc) metaDesc.setAttribute('content', "Application web d'analyse SEO avec intégration Google Search Console");
            upsertMetaTag('property', 'og:title', 'Metrics');
            upsertMetaTag('property', 'og:description', "Application web d'analyse SEO avec intégration Google Search Console");
            upsertMetaTag('property', 'og:type', 'website');
            upsertMetaTag('property', 'og:url', typeof window !== 'undefined' ? window.location.origin : "https://ascan.io");
            upsertMetaTag('property', 'og:image', typeof window !== 'undefined' ? `${window.location.origin}/favicon-metrics.svg` : "https://ascan.io/favicon-metrics.svg");
            upsertMetaTag('name', 'twitter:card', 'summary_large_image');
            upsertMetaTag('name', 'twitter:title', 'Metrics');
            upsertMetaTag('name', 'twitter:description', "Application web d'analyse SEO avec intégration Google Search Console");
            upsertMetaTag('name', 'twitter:image', typeof window !== 'undefined' ? `${window.location.origin}/favicon-metrics.svg` : "https://ascan.io/favicon-metrics.svg");
        };
    }, [step]);

    // Favicon : landing audit → favicon.svg ; au démontage → favicon-metrics.svg (app)
    useEffect(() => {
        const link = document.querySelector('link[rel="icon"]');
        if (!link) return;
        link.setAttribute('href', '/favicon.svg');
        link.setAttribute('type', 'image/svg+xml');
        return () => {
            link.setAttribute('href', '/favicon-metrics.svg');
            link.setAttribute('type', 'image/svg+xml');
        };
    }, []);

    useEffect(() => {
        // Persiste les paramètres UTM/click IDs du tunnel pour les redirections suivantes.
        const qs = getTrackingSearchFromCurrentUrl();
        persistTrackingSearch(qs);
    }, []);

    useEffect(() => {
        trackPublicEvent({ eventType: 'page_view', eventLabel: 'landing_page_view', extraData: { referrer: document.referrer || null, screen: `${window.innerWidth}x${window.innerHeight}` } });
    }, []);

    useEffect(() => { stepRef.current = step; }, [step]);
    useEffect(() => { urlRef.current = url; }, [url]);

    useEffect(() => {
        const trackLandingExit = (reason = 'leave') => {
            if (landingExitTrackedRef.current) return;
            landingExitTrackedRef.current = true;
            const duration = Math.max(0, Math.round((Date.now() - landingStartRef.current) / 1000));
            sendBeaconPublicEvent({
                eventType: 'session_end',
                eventLabel: 'landing_exit',
                duration,
                extraData: {
                    reason,
                    step: stepRef.current,
                    had_url: !!urlRef.current.trim(),
                    submitted: auditSubmittedRef.current,
                },
            });
        };

        const onBeforeUnload = () => trackLandingExit('beforeunload');
        const onPageHide = () => trackLandingExit('pagehide');
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') trackLandingExit('hidden');
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('beforeunload', onBeforeUnload);
            trackLandingExit('unmount');
        };
    }, []);

    useEffect(() => {
        if (step === 2) trackPublicEvent({ eventType: 'page_view', eventLabel: 'landing_step_2_view', extraData: { url: url || null } });
    }, [step]);

    const handleStep1 = async (e) => {
        e.preventDefault();
        setError('');
        setErrorStatus(null);
        let normalized = url.trim();
        if (!normalized) { setErrorStatus(null); return setError('Veuillez entrer l\'URL de votre site'); }
        if (!/^https?:\/\//i.test(normalized)) normalized = 'https://' + normalized;
        normalized = normalized.replace(/\/$/, '');

        setLoading(true);
        try {
            const checkRes = await fetch(`${API_BASE_URL}/api/public/check-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: normalized }),
            });
            const checkData = await checkRes.json();
            if (checkData.ok === false) {
                setLoading(false);
                setErrorStatus(checkData.status ?? null);
                return setError(checkData.message || 'Cette page n\'est pas accessible.');
            }
        } catch (_) {
            setLoading(false);
            return setError('Impossible de vérifier l\'URL. Réessayez.');
        }

        setUrl(normalized);
        trackPublicEvent({ eventType: 'form_submit', eventLabel: 'landing_step_1_url', extraData: { step: 1, url: normalized } });

        // Mode quick : skip le formulaire de contact et lance directement l'audit
        if (quickMode) {
            setAuditStarted(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/public/audit/quick`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: normalized }),
                });
                if (!response.ok) {
                    const data = await response.json();
                    throw new Error(data.detail || 'Erreur lors du lancement de l\'audit');
                }
                const data = await response.json();
                navigate(`/audit/${data.token}`);
            } catch (err) {
                setAuditStarted(false);
                setLoading(false);
                setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
            }
            return;
        }

        setLoading(false);
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!firstName.trim()) return setError('Veuillez entrer votre prénom');
        if (!lastName.trim()) return setError('Veuillez entrer votre nom');
        if (!email.trim()) return setError('Veuillez entrer votre adresse email');
        if (!isValidEmail(email)) return setError('Veuillez entrer une adresse email valide (ex. vous@domaine.fr)');
        if (!phone.trim()) return setError('Veuillez entrer votre numéro de téléphone');
        if (!isValidPhone(phone)) return setError('Veuillez entrer un numéro de téléphone valide (au moins 10 chiffres)');
        if (!rgpdConsent) return setError('Veuillez accepter la politique de confidentialité');

        trackPublicEvent({ eventType: 'form_submit', eventLabel: 'landing_form_contact_submit', extraData: { url: url.trim(), has_company: !!company.trim() } });
        auditSubmittedRef.current = true;

        // URL déjà validée (404/403) à l'étape 1 ; normaliser comme le backend
        let normalizedUrl = url.trim();
        if (!/^https?:\/\//i.test(normalizedUrl)) normalizedUrl = 'https://' + normalizedUrl;
        normalizedUrl = normalizedUrl.replace(/\/$/, '');

        setAuditStarted(true);
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/public/audit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    url: normalizedUrl,
                    email: email.trim(),
                    first_name: firstName.trim() || null,
                    last_name: lastName.trim() || null,
                    phone: phone.trim() || null,
                    company: company.trim() || null,
                }),
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Erreur lors de la soumission');
            }
            const data = await response.json();
            trackPublicEvent({ eventType: 'form_submit', eventLabel: 'landing_form_contact_success', extraData: { token: data.token, url: url.trim() } });
            // Sur audit.ascan.io : navigation pleine page (plus fiable que client-side seul dans les WebViews in-app / Safari mobile)
            const token = data.token;
            const trackingSearch = getTrackingSearchFromCurrentUrl() || getPersistedTrackingSearch();
            persistTrackingSearch(trackingSearch);
            if (typeof window !== 'undefined' && window.location.hostname === 'audit.ascan.io' && token) {
                window.location.assign(`${window.location.origin}/audit/${encodeURIComponent(token)}${trackingSearch}`);
                return;
            }
            navigate(`/audit/${token}${trackingSearch}`);
        } catch (err) {
            auditSubmittedRef.current = false;
            setAuditStarted(false);
            setLoading(false);
            setErrorStatus(null);
            setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
            trackPublicEvent({ eventType: 'form_submit', eventLabel: 'landing_form_contact_error', extraData: { url: url.trim(), error: (err.message || '').slice(0, 100) } });
        }
    };

    return (
        <div className="min-h-screen flex flex-col relative">
            <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 pt-6 pb-8 sm:pt-8 sm:pb-10 md:py-12 max-w-6xl mx-auto w-full">

                {/* ── ÉTAPE 1 : URL + preview ──────────────────── */}
                {step === 1 && (
                    <div className="w-full max-w-5xl landing-step-in">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

                            {/* Colonne gauche */}
                            <div className="flex flex-col gap-6 md:gap-8">
                                {/* Heading */}
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4 md:mb-6"
                                        style={{ background: 'rgba(0, 222, 222, 0.10)', color: '#00a0a0' }}>
                                        <Zap size={12} />Résultats en moins d'une minute
                                    </div>
                                    <h1 className="font-heading text-[2rem] sm:text-4xl md:text-5xl font-light text-neutral-900 leading-[1.15] tracking-tight mb-3 md:mb-4">
                                        <strong className="font-semibold">Audit SEO & GEO</strong><br />
                                        <span className="text-2xl sm:text-3xl md:text-3xl">gratuit & instantané</span>
                                    </h1>
                                    <p className="text-sm md:text-base text-neutral-500 leading-relaxed max-w-sm">
                                        Découvrez ce qui freine votre visibilité sur Google — et comment les IA comprennent votre site.
                                    </p>
                                </div>

                                {/* Bénéfices */}
                                <ul className="flex flex-col gap-3 md:gap-4">
                                    {[
                                        { icon: BarChart3, text: 'Score SEO détaillé par catégorie', color: '#6366f1' },
                                        { icon: Sparkles, text: 'Analyse IA de votre positionnement', color: '#00a0a0' },
                                        { icon: Shield, text: '100 % gratuit, sans engagement', color: '#059669' },
                                    ].map(({ icon: Icon, text, color }) => (
                                        <li key={text} className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}12` }}>
                                                <Icon size={15} style={{ color }} />
                                            </div>
                                            <span className="text-sm text-neutral-700">{text}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* Formulaire URL */}
                                <div>
                                    <form onSubmit={handleStep1} className="flex flex-col gap-3 sm:gap-4">
                                        <div className="relative">
                                            <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
                                            <input
                                                type="text"
                                                inputMode="url"
                                                placeholder="monnomdedomaine.com"
                                                value={url}
                                                onChange={(e) => setUrl(e.target.value)}
                                                autoComplete="url"
                                                className="w-full pl-11 pr-4 py-3.5 sm:py-4 min-h-[48px] rounded-full border text-gray-900 placeholder:text-gray-400 transition-all outline-none bg-white text-sm"
                                                style={{ borderColor: '#e2e8f0' }}
                                                onFocus={e => inputFocusStyle(e, true)}
                                                onBlur={e => inputFocusStyle(e, false)}
                                            />
                                        </div>
                                        {error && (
                                            <div className="px-4 py-3 rounded-xl text-sm" style={errorStatus === 403 ? { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#92400e' } : { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#b91c1c' }}>
                                                {errorStatus === 403 && (
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Lock size={18} className="flex-shrink-0" />
                                                        <span className="font-semibold">Ce site bloque les analyses automatiques</span>
                                                    </div>
                                                )}
                                                <p className={errorStatus === 403 ? 'text-amber-900/90' : ''}>{error}</p>
                                            </div>
                                        )}
                                        <button type="submit" disabled={loading || !step1Valid} className="w-full py-3.5 sm:py-4 font-semibold rounded-full text-base text-white flex items-center justify-center gap-2 cta-audit-gratuit min-h-[48px] sm:min-h-[52px] touch-manipulation disabled:opacity-70 disabled:pointer-events-none">
                                            {loading ? 'Vérification en cours…' : <>Analyser mon site <ArrowRight size={16} /></>}
                                        </button>
                                    </form>
                                    <div className="mt-2 md:mt-3 flex items-center gap-2 sm:gap-3 text-[11px] text-neutral-400">
                                        <span className="flex items-center gap-1"><Shield size={10} style={{ color: '#059669' }} />Gratuit</span>
                                        <span className="flex items-center gap-1"><Zap size={10} style={{ color: '#00a0a0' }} />Sans engagement</span>
                                        <span className="flex items-center gap-1"><CheckCircle size={10} style={{ color: '#6366f1' }} />Instantané</span>
                                    </div>
                                </div>
                            </div>

                            {/* Colonne droite : illustration / aperçu rapport (sous le formulaire sur mobile) */}
                            <div className="w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
                                <AuditPreview url={url} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── ÉTAPE 2 : Infos perso + teaser ─────────── */}
                {step === 2 && (
                    <div className="w-full landing-step-in">
                        {/* Barre de progression */}
                        {!auditStarted && (
                            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 md:mb-8 max-w-5xl mx-auto w-full">
                                <button type="button" onClick={() => { trackPublicEvent({ eventType: 'cta_click', eventLabel: 'landing_step_2_back' }); setStep(1); setError(''); setErrorStatus(null); setAuditStarted(false); }}
                                    className="text-sm text-neutral-400 hover:text-neutral-700 active:text-neutral-800 transition-colors min-h-[44px] min-w-[44px] flex items-center flex-shrink-0 touch-manipulation">
                                    ← Retour
                                </button>
                                <div className="flex-1 min-w-0 flex items-center gap-1.5 sm:gap-2">
                                    <div className="h-1.5 flex-1 rounded-full bg-neutral-100 overflow-hidden min-w-0">
                                        <div className="h-full rounded-full" style={{ width: '100%', background: '#00dede' }} />
                                    </div>
                                    <div className="h-1.5 flex-1 rounded-full bg-neutral-100 overflow-hidden min-w-0">
                                        <div className="h-full rounded-full transition-all duration-500" style={{ width: '30%', background: '#00dede', opacity: 0.35 }} />
                                    </div>
                                </div>
                                <span className="text-xs text-neutral-400 font-medium whitespace-nowrap flex-shrink-0">Étape 2 / 2</span>
                            </div>
                        )}

                        {/* Layout : 2 colonnes avant soumission, 1 colonne centrée après */}
                        <div className={`max-w-5xl mx-auto w-full transition-all duration-500 ${
                            auditStarted
                                ? 'flex justify-center'
                                : 'grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 items-start'
                        }`}>

                            {/* Formulaire (masqué après soumission) */}
                            {!auditStarted && (
                                <div className="rounded-2xl p-3 sm:p-4 md:p-5 bg-white border landing-step-in w-full"
                                    style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.08)', borderColor: 'rgba(0,0,0,0.06)' }}>
                                    <h2 className="font-heading text-base md:text-xl font-light text-neutral-900 mb-0.5">
                                        Plus qu'une étape pour <strong className="font-semibold">accéder à votre audit</strong>
                                    </h2>
                                    <p className="text-xs sm:text-sm text-neutral-500 mb-3 sm:mb-4">Coordonnées pour lancer l'analyse et recevoir le rapport.</p>

                                    <form onSubmit={handleSubmit} className="flex flex-col gap-2.5 sm:gap-3">
                                        <div className="grid grid-cols-2 gap-2 sm:gap-2.5 landing-field-in" style={{ animationDelay: '0ms' }}>
                                            <div>
                                                <label htmlFor="pub-firstname" className="block text-xs font-semibold text-neutral-600 mb-1">
                                                    Prénom <span style={{ color: '#00dede' }}>*</span>
                                                </label>
                                                <div className="relative">
                                                    <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                                                    <input id="pub-firstname" type="text" placeholder="Jean" value={firstName}
                                                        onChange={e => setFirstName(e.target.value)}
                                                        autoComplete="given-name"
                                                        className={inputBase}
                                                        style={{ borderColor: '#e2e8f0' }}
                                                        onFocus={e => inputFocusStyle(e, true)} onBlur={e => inputFocusStyle(e, false)} />
                                                </div>
                                            </div>
                                            <div>
                                                <label htmlFor="pub-lastname" className="block text-xs font-semibold text-neutral-600 mb-1">
                                                    Nom <span style={{ color: '#00dede' }}>*</span>
                                                </label>
                                                <div className="relative">
                                                    <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                                                    <input id="pub-lastname" type="text" placeholder="Dupont" value={lastName}
                                                        onChange={e => setLastName(e.target.value)}
                                                        autoComplete="family-name"
                                                        className={inputBase}
                                                        style={{ borderColor: '#e2e8f0' }}
                                                        onFocus={e => inputFocusStyle(e, true)} onBlur={e => inputFocusStyle(e, false)} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5 landing-field-in" style={{ animationDelay: '45ms' }}>
                                            <div>
                                                <label htmlFor="pub-email" className="block text-xs font-semibold text-neutral-600 mb-1">
                                                    Email <span style={{ color: '#00dede' }}>*</span>
                                                </label>
                                                <div className="relative">
                                                    <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                                                    <input id="pub-email" type="email" inputMode="email" placeholder="vous@email.com" value={email}
                                                        onChange={e => setEmail(e.target.value)}
                                                        autoComplete="email"
                                                        className={inputBase}
                                                        style={{ borderColor: email.trim() && !isValidEmail(email) ? '#ef4444' : '#e2e8f0' }}
                                                        onFocus={e => inputFocusStyle(e, true)} onBlur={e => inputFocusStyle(e, false)} />
                                                </div>
                                                {email.trim() && !isValidEmail(email) && (
                                                    <p className="mt-1 text-xs text-red-500">Entrez une adresse email valide (ex. vous@domaine.fr)</p>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="pub-phone" className="block text-xs font-semibold text-neutral-600 mb-1">
                                                    Téléphone <span style={{ color: '#00dede' }}>*</span>
                                                </label>
                                                <div className="relative">
                                                    <Phone size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                                                    <input id="pub-phone" type="tel" inputMode="tel" placeholder="06 12 34 56 78" value={phone}
                                                        onChange={e => setPhone(sanitizePhoneInput(e.target.value))}
                                                        autoComplete="tel"
                                                        className={inputBase}
                                                        style={{ borderColor: phone.trim() && !isValidPhone(phone) ? '#ef4444' : '#e2e8f0' }}
                                                        onFocus={e => inputFocusStyle(e, true)} onBlur={e => inputFocusStyle(e, false)} />
                                                </div>
                                                {phone.trim() && !isValidPhone(phone) && (
                                                    <p className="mt-1 text-xs text-red-500">Entrez un numéro valide (10 à 15 chiffres, ex. 06 12 34 56 78)</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="landing-field-in" style={{ animationDelay: '90ms' }}>
                                            <label htmlFor="pub-company" className="block text-xs font-semibold text-neutral-600 mb-1">Entreprise (optionnel)</label>
                                            <div className="relative">
                                                <Building2 size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                                                <input id="pub-company" type="text" placeholder="Mon entreprise" value={company}
                                                    onChange={e => setCompany(e.target.value)}
                                                    autoComplete="organization"
                                                    className={inputBase}
                                                    style={{ borderColor: '#e2e8f0' }}
                                                    onFocus={e => inputFocusStyle(e, true)} onBlur={e => inputFocusStyle(e, false)} />
                                            </div>
                                        </div>

                                        <label className="flex items-start gap-2.5 cursor-pointer mt-0.5 landing-field-in" style={{ animationDelay: '135ms' }}>
                                            <input
                                                type="checkbox"
                                                checked={rgpdConsent}
                                                onChange={e => setRgpdConsent(e.target.checked)}
                                                style={{ accentColor: '#00a0a0' }}
                                                className="mt-0.5 w-4 h-4 flex-shrink-0 rounded-md border-2 border-neutral-300 bg-white transition-colors focus:ring-2 focus:ring-[#00a0a0]/35 focus:ring-offset-0 focus:border-[#00a0a0]"
                                            />
                                            <span className="text-[11px] sm:text-xs text-neutral-500 leading-snug">
                                                J&apos;accepte la{' '}
                                                <button type="button"
                                                    onClick={e => { e.preventDefault(); trackPublicEvent({ eventType: 'cta_click', eventLabel: 'landing_modal_privacy_open' }); setShowPrivacyModal(true); }}
                                                    className="underline hover:no-underline font-medium focus:outline-none"
                                                    style={{ color: '#00a0a0' }}>
                                                    politique de confidentialité
                                                </button>
                                                {' '}pour recevoir mon audit. <span style={{ color: '#00dede' }}>*</span>
                                            </span>
                                        </label>

                                        {error && (
                                            <div className="px-3 py-2.5 rounded-xl text-sm landing-field-in" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#b91c1c' }}>
                                                {error}
                                            </div>
                                        )}

                                        <div className="landing-field-in" style={{ animationDelay: '180ms' }}>
                                            <button type="submit" disabled={loading || !step2Valid}
                                                className="w-full py-3 sm:py-3.5 font-semibold rounded-full text-base text-white flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cta-audit-gratuit min-h-[48px] touch-manipulation">
                                                {loading ? 'Lancement…' : 'Lancer l\'analyse'} <ArrowRight size={16} />
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* Teaser audit — masqué sur mobile quand le formulaire est affiché */}
                            <div className={`rounded-2xl p-4 sm:p-6 bg-white border transition-all duration-500 ${
                                auditStarted ? 'w-full max-w-md mx-auto' : 'hidden lg:block'
                            }`} style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.06)', borderColor: 'rgba(0,0,0,0.06)' }}>
                                <div className="flex items-center gap-2 mb-5">
                                    <div className={`w-2 h-2 rounded-full animate-pulse ${auditStarted ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                                    <span className="text-xs font-semibold text-neutral-600">
                                        {auditStarted ? 'Analyse en cours…' : 'Analyse en pause — complétez le formulaire'}
                                    </span>
                                </div>
                                <AuditTeaser url={url} started={auditStarted} />
                            </div>
                        </div>

                        {!auditStarted && (
                            <div className="mt-3 md:mt-5 flex items-center justify-center gap-3 sm:gap-5 text-[11px] text-neutral-500 max-w-4xl mx-auto">
                                <span className="flex items-center gap-1"><Shield size={10} style={{ color: '#059669' }} />100 % gratuit</span>
                                <span className="flex items-center gap-1"><Zap size={10} style={{ color: '#00a0a0' }} />Sans engagement</span>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Modale confidentialité */}
            {showPrivacyModal && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPrivacyModal(false)} aria-hidden />
                    <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
                            <h2 className="font-heading text-lg font-semibold text-neutral-900">Politique de confidentialité</h2>
                            <button type="button" onClick={() => setShowPrivacyModal(false)}
                                className="p-2 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" aria-label="Fermer">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-5 overflow-y-auto flex-1">{PRIVACY_POLICY_CONTENT}</div>
                        <div className="p-5 border-t border-neutral-100">
                            <button type="button" onClick={() => setShowPrivacyModal(false)}
                                className="w-full py-3 px-4 font-semibold rounded-xl cta-audit-gratuit text-white">
                                J&apos;ai compris
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
